import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";

const DEFAULT_ESTIMATE_HEIGHT = 160;
const DEFAULT_ROOT_MARGIN = "320px 0px 320px 0px";

const normalizeEstimateHeight = (estimateHeight) => {
  const candidate = Number.isFinite(estimateHeight) && estimateHeight !== 0
    ? estimateHeight
    : DEFAULT_ESTIMATE_HEIGHT;
  return Math.max(candidate, 1);
};

export default function VirtualizedItem({
  children,
  className = "",
  dataAttributes = {},
  disabled = false,
  estimateHeight = DEFAULT_ESTIMATE_HEIGHT,
  rootMargin = DEFAULT_ROOT_MARGIN,
  rootRef = null,
}) {
  const supportsVirtualization = useMemo(() => {
    if (disabled || typeof window === "undefined") {
      return false;
    }
    return "IntersectionObserver" in window && "ResizeObserver" in window;
  }, [disabled]);

  const normalizedEstimateHeight = useMemo(() => normalizeEstimateHeight(estimateHeight), [estimateHeight]);
  const placeholderRef = useRef(null);
  const contentRef = useRef(null);
  const isVisibleRef = useRef(!supportsVirtualization);
  const [isVisible, setIsVisible] = useState(!supportsVirtualization);
  const [measuredHeight, setMeasuredHeight] = useState(() => normalizedEstimateHeight);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    setMeasuredHeight((currentHeight) => {
      if (isVisibleRef.current || Math.abs(currentHeight - normalizedEstimateHeight) <= 1) {
        return currentHeight;
      }
      return normalizedEstimateHeight;
    });
  }, [normalizedEstimateHeight]);

  useEffect(() => {
    if (!supportsVirtualization) {
      setIsVisible(true);
      return undefined;
    }

    const placeholder = placeholderRef.current;
    if (!placeholder) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting || entry.intersectionRatio > 0);
        });
      },
      {
        root: rootRef?.current ?? null,
        rootMargin,
        threshold: [0, 0.01, 0.1],
      },
    );

    observer.observe(placeholder);
    return () => observer.disconnect();
  }, [rootMargin, rootRef, supportsVirtualization]);

  useEffect(() => {
    if (!supportsVirtualization || !isVisible) {
      return undefined;
    }

    const element = contentRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = element.getBoundingClientRect().height;
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
        return;
      }
      setMeasuredHeight((currentHeight) => (
        Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight
      ));
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [isVisible, normalizedEstimateHeight, supportsVirtualization]);

  if (!supportsVirtualization) {
    return <>{children}</>;
  }

  return (
    <div
      ref={placeholderRef}
      className={classNames("virtualized-item", className)}
      style={{ minHeight: Math.max(1, Math.round(measuredHeight)), width: "100%" }}
      {...dataAttributes}
    >
      {isVisible ? (
        <div ref={contentRef}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

VirtualizedItem.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  dataAttributes: PropTypes.object,
  disabled: PropTypes.bool,
  estimateHeight: PropTypes.number,
  rootMargin: PropTypes.string,
  rootRef: PropTypes.shape({
    current: PropTypes.any,
  }),
};
